import { rememberListLocation } from '@/composables/useBreadcrumb';
import { loadCurrentUser, useCurrentUser } from '@/composables/useCurrentUser';
import AppLayout from '@/layout/AppLayout.vue';
import ToastEventBus from 'primevue/toasteventbus';
import { nextTick } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

// A List sits at /{group}/{page}, the menu group followed by the menu label. A View sits under its
// aggregate with its id; a View that belongs to a parent carries the parent ids in its path. Every id
// is a GUID, which also keeps /customers/all from matching the Customer View.
const GUID = '([0-9a-fA-F-]{36})';

// meta.breadcrumb holds the crumbs above the record; a crumb with `to` links to its screen. A View
// adds the record's own crumbs through useBreadcrumb once its data has loaded.
// meta.list names the List a route shows, and a crumb's `list` the List it leads back to: the crumb
// returns to where the user last was in that List, its filters, page and sort, rather than to its
// defaults. The three customer Lists share one, so the crumb returns to whichever was open.
// meta.policy is the InternalApi policy a user must pass to open the screen: the edit policy of the
// aggregate it manages. The menu and GatedLink read it from here too. The customer area has none:
// every settlement View links into it, so it opens read-only for everyone.
const customers = { label: 'Customers', to: { name: 'customers-all' }, list: 'customers' };
const chargerDefinitions = { label: 'Charger definitions', to: { name: 'chargerdefinitions-list' }, list: 'chargerdefinitions' };
const affiliates = { label: 'Affiliates', to: { name: 'affiliates-list' }, list: 'affiliates' };
const registrations = { label: 'Registrations', to: { name: 'registrations-list' }, list: 'registrations' };
const transactions = { label: 'Transactions', to: { name: 'transactions-list' }, list: 'transactions' };
const payouts = { label: 'Payouts', to: { name: 'payouts-list' }, list: 'payouts' };
const ledgers = { label: 'Ledgers', to: { name: 'ledgers-list' }, list: 'ledgers' };
const conversionFactors = { label: 'Conversion factors', to: { name: 'conversionfactors-list' }, list: 'conversionfactors' };

const routes = [
    {
        path: '/',
        component: AppLayout,
        children: [
            {
                path: '',
                redirect: { name: 'dashboard-overview' }
            },
            {
                path: '/dashboard/overview',
                name: 'dashboard-overview',
                meta: {
                    breadcrumb: [{ label: 'Overview' }]
                },
                component: () => import('@/views/dashboard/DashboardView.vue')
            },
            {
                path: '/customers/all',
                name: 'customers-all',
                meta: {
                    breadcrumb: [{ label: 'Customers' }],
                    list: 'customers'
                },
                component: () => import('@/views/zeres/customers/List.vue')
            },
            {
                // the same List with the customer type preset; the menu offers all three
                path: '/customers/consumer',
                name: 'customers-consumer',
                meta: {
                    breadcrumb: [{ label: 'Consumer' }],
                    list: 'customers',
                    customerType: 'Consumer'
                },
                component: () => import('@/views/zeres/customers/List.vue')
            },
            {
                path: '/customers/business',
                name: 'customers-business',
                meta: {
                    breadcrumb: [{ label: 'Business' }],
                    list: 'customers',
                    customerType: 'Business'
                },
                component: () => import('@/views/zeres/customers/List.vue')
            },
            {
                // the contracts and where each stands on compliance; the same List with the status
                // preset, the menu offers all four. A row opens the Contract View
                path: '/contracts/all',
                name: 'contracts-all',
                meta: {
                    policy: 'CanViewCompliance',
                    breadcrumb: [{ label: 'Contracts' }],
                    list: 'contracts'
                },
                component: () => import('@/views/zeres/contracts/List.vue')
            },
            {
                path: '/contracts/compliant',
                name: 'contracts-compliant',
                meta: {
                    policy: 'CanViewCompliance',
                    breadcrumb: [{ label: 'Compliant' }],
                    list: 'contracts',
                    complianceStatus: 'Compliant'
                },
                component: () => import('@/views/zeres/contracts/List.vue')
            },
            {
                path: '/contracts/pending',
                name: 'contracts-pending',
                meta: {
                    policy: 'CanViewCompliance',
                    breadcrumb: [{ label: 'Pending' }],
                    list: 'contracts',
                    complianceStatus: 'Pending'
                },
                component: () => import('@/views/zeres/contracts/List.vue')
            },
            {
                path: '/contracts/not-compliant',
                name: 'contracts-not-compliant',
                meta: {
                    policy: 'CanViewCompliance',
                    breadcrumb: [{ label: 'Not compliant' }],
                    list: 'contracts',
                    complianceStatus: 'NotCompliant'
                },
                component: () => import('@/views/zeres/contracts/List.vue')
            },
            {
                // the calls Rinkel reported, newest first; no view, a row links to its customer and its
                // row action links one.
                // Administrators only for now, while the page is tried out
                path: '/communication/calls',
                name: 'calls-list',
                meta: {
                    policy: 'CanViewCall',
                    breadcrumb: [{ label: 'Calls' }]
                },
                component: () => import('@/views/zeres/calls/List.vue')
            },
            {
                path: `/customers/:customerId${GUID}`,
                name: 'customers-view',
                meta: {
                    breadcrumb: [customers]
                },
                component: () => import('@/views/zeres/customers/View.vue')
            },
            {
                // no list route: a customer's locations are listed on the Customer View
                path: `/customers/:customerId${GUID}/locations/:locationId${GUID}`,
                name: 'locations-view',
                meta: {
                    breadcrumb: [customers]
                },
                component: () => import('@/views/zeres/locations/View.vue')
            },
            // the Contracts List opens it, as do a customer's locations
            {
                path: `/customers/:customerId${GUID}/locations/:locationId${GUID}/contracts/:contractId${GUID}`,
                name: 'contracts-view',
                meta: {
                    breadcrumb: [customers]
                },
                component: () => import('@/views/zeres/contracts/View.vue')
            },
            {
                // no list route: a location's charge points are listed on the Location View
                path: `/customers/:customerId${GUID}/locations/:locationId${GUID}/chargepoints/:chargePointId${GUID}`,
                name: 'chargepoints-view',
                meta: {
                    breadcrumb: [customers]
                },
                component: () => import('@/views/zeres/chargepoints/View.vue')
            },
            {
                path: '/reference-data/charger-definitions',
                name: 'chargerdefinitions-list',
                meta: {
                    policy: 'CanEditChargerDefinition',
                    breadcrumb: [{ label: 'Charger definitions' }],
                    list: 'chargerdefinitions'
                },
                component: () => import('@/views/zeres/chargerdefinitions/List.vue')
            },
            {
                path: `/reference-data/charger-definitions/:id${GUID}`,
                name: 'chargerdefinitions-view',
                meta: {
                    policy: 'CanEditChargerDefinition',
                    breadcrumb: [chargerDefinitions]
                },
                component: () => import('@/views/zeres/chargerdefinitions/View.vue')
            },
            {
                path: '/reference-data/affiliates',
                name: 'affiliates-list',
                meta: {
                    policy: 'CanEditAffiliate',
                    breadcrumb: [{ label: 'Affiliates' }],
                    list: 'affiliates'
                },
                component: () => import('@/views/zeres/affiliates/List.vue')
            },
            {
                path: `/reference-data/affiliates/:id${GUID}`,
                name: 'affiliates-view',
                meta: {
                    policy: 'CanEditAffiliate',
                    breadcrumb: [affiliates]
                },
                component: () => import('@/views/zeres/affiliates/View.vue')
            },
            {
                // first step of the settlement chain
                path: '/settlement/registrations',
                name: 'registrations-list',
                meta: {
                    policy: 'CanEditRegistration',
                    breadcrumb: [{ label: 'Registrations' }],
                    list: 'registrations'
                },
                component: () => import('@/views/zeres/registrations/List.vue')
            },
            {
                path: `/settlement/registrations/:id${GUID}`,
                name: 'registrations-view',
                meta: {
                    policy: 'CanEditRegistration',
                    breadcrumb: [registrations]
                },
                component: () => import('@/views/zeres/registrations/View.vue')
            },
            {
                path: '/settlement/transactions',
                name: 'transactions-list',
                meta: {
                    policy: 'CanEditTransaction',
                    breadcrumb: [{ label: 'Transactions' }],
                    list: 'transactions'
                },
                component: () => import('@/views/zeres/transactions/List.vue')
            },
            {
                path: `/settlement/transactions/:id${GUID}`,
                name: 'transactions-view',
                meta: {
                    policy: 'CanEditTransaction',
                    breadcrumb: [transactions]
                },
                component: () => import('@/views/zeres/transactions/View.vue')
            },
            {
                path: '/settlement/payouts',
                name: 'payouts-list',
                meta: {
                    policy: 'CanEditPayout',
                    breadcrumb: [{ label: 'Payouts' }],
                    list: 'payouts'
                },
                component: () => import('@/views/zeres/payouts/List.vue')
            },
            {
                path: `/settlement/payouts/:id${GUID}`,
                name: 'payouts-view',
                meta: {
                    policy: 'CanEditPayout',
                    breadcrumb: [payouts]
                },
                component: () => import('@/views/zeres/payouts/View.vue')
            },
            {
                // no menu entry: the menu links each ledger's View directly
                path: '/accounting/ledgers',
                name: 'ledgers-list',
                meta: {
                    policy: 'CanEditLedger',
                    breadcrumb: [{ label: 'Ledgers' }],
                    list: 'ledgers'
                },
                component: () => import('@/views/zeres/ledgers/List.vue')
            },
            {
                path: `/accounting/ledgers/:ledgerId${GUID}`,
                name: 'ledgers-view',
                meta: {
                    policy: 'CanEditLedger',
                    breadcrumb: [ledgers]
                },
                component: () => import('@/views/zeres/ledgers/View.vue')
            },
            {
                path: `/accounting/ledgers/:ledgerId${GUID}/accounts/:accountId${GUID}`,
                name: 'accounts-view',
                meta: {
                    policy: 'CanEditAccount',
                    breadcrumb: [ledgers]
                },
                component: () => import('@/views/zeres/accounts/View.vue')
            },
            {
                // the service provider settings, the one row the back office configures
                path: '/settings/service-provider',
                name: 'settings-service-provider',
                meta: {
                    policy: 'CanEditServiceProviderSettings',
                    breadcrumb: [{ label: 'Service provider settings' }]
                },
                component: () => import('@/views/zeres/settings/Settings.vue')
            },
            {
                // the net and renewable factors per year and the expected ERE price, kept by Administrators
                path: '/settings/conversion-factors',
                name: 'conversionfactors-list',
                meta: {
                    policy: 'CanEditConversionFactor',
                    breadcrumb: [{ label: 'Conversion factors' }],
                    list: 'conversionfactors'
                },
                component: () => import('@/views/zeres/conversionfactors/List.vue')
            },
            {
                path: `/settings/conversion-factors/:id${GUID}`,
                name: 'conversionfactors-view',
                meta: {
                    policy: 'CanEditConversionFactor',
                    breadcrumb: [conversionFactors]
                },
                component: () => import('@/views/zeres/conversionfactors/View.vue')
            }
        ]
    },
    {
        path: '/pages/notfound',
        name: 'notfound',
        component: () => import('@/views/pages/NotFound.vue')
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('@/views/pages/NotFound.vue')
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior() {
        return { left: 0, top: 0 };
    }
});

// /me is read before the first screen renders, so every page and command knows the user's
// permissions from the start; later navigations reuse the first read. A screen the user has no
// policy for, reached through a bookmark or a typed URL, sends them to the dashboard. The toast goes
// through PrimeVue's event bus: a guard runs outside any component, where useToast cannot be injected.
router.beforeEach(async (to) => {
    await loadCurrentUser();
    if (useCurrentUser().can(to.meta.policy)) return true;

    const crumbs = to.meta.breadcrumb ?? [];
    const page = crumbs.length ? crumbs[crumbs.length - 1].label : 'that page';
    // the Toast lives in AppLayout, which renders only once the first navigation has resolved
    const notice = { severity: 'warn', summary: `You have no access to ${page}`, life: 4000 };
    router
        .isReady()
        .then(() => nextTick())
        .then(() => ToastEventBus.emit('add', notice));
    return { name: 'dashboard-overview' };
});

// a List keeps its filters, page and sort in the query, so its full path is where the user was
router.afterEach((to, _from, failure) => {
    if (!failure && to.meta.list) rememberListLocation(to.meta.list, to.fullPath);
});

export default router;
